"use client";

import type { ILink } from "@components/core/Link";
import type { StorySlidesBlock } from "@mw/shared/payload-types";
import type { IImage } from "@ui/components/types/media";
import type { MediaRemoteControl } from "@vidstack/react";

import { formatLink, getVideoUrl } from "@/app/_utilities/payload";
import { Image } from "@components/core/Image";
import { LazyVideoPlayer } from "@components/core/LazyVideoPlayer";
import { Link } from "@components/core/Link";
import SliderGeneric from "@ui/components/composers/SliderGenericWrapper";
import { AspectRatio } from "@ui/components/core/AspectRatio";
import { Button } from "@ui/components/core/Button";
import { CircleProgressIndicator } from "@ui/components/core/CircleProgressIndicator";
import HeadingWithLink from "@ui/components/headings/HeadingWithLink";
import { useIsLargeOrAbove } from "@ui/hooks/useBreakpoint";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import { ArrowUpRight, Pause, Play, Volume2, VolumeX } from "lucide-react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Block } from "./Block";

interface StorySlideButtonProps {
  description?: null | string;
  inverse?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  progress?: number;
  slideId: string;
  title: string;
}

const StorySlideButton = ({ description, isActive, onClick, progress, title, inverse }: StorySlideButtonProps) => {
  return (
    <button className={cn("relative w-full text-left")} onClick={onClick}>
      {isActive && progress !== undefined && (
        <CircleProgressIndicator className='absolute -left-24 top-2' inverse={inverse} progress={progress} />
      )}
      <h2 className={cn("f-description-03 mb-7 font-bold uppercase", !isActive && "text-lightest")}>{title}</h2>
      {description && <h3 className={cn("f-description-03", !isActive && "text-lightest")}>{description}</h3>}
    </button>
  );
};

const PlayPauseVideoButton = ({
  isPlaying,
  togglePlay,
}: {
  isPlaying: boolean;
  togglePlay: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const t = useTranslations("ClientKeys.Globals");
  return (
    <Button
      className={cn(
        "effect-opacity pointer-events-auto absolute right-8 top-8 z-30 gap-8 opacity-0 group-hover:opacity-100",
        !isPlaying && "opacity-100",
      )}
      onClick={e => togglePlay(e)}
      variant='primary'
    >
      {isPlaying ? <Pause className='fill-white' size={16} /> : <Play className='fill-white' size={16} />}
      {isPlaying ? t("pause") : t("play")}
    </Button>
  );
};

const ToggleMuteButton = ({
  isMuted,
  onClick,
}: {
  isMuted: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
  <Button className='absolute left-8 top-8 z-30' onClick={onClick} variant='primary'>
    {!isMuted ? <Volume2 className='fill-white' size={16} /> : <VolumeX className='fill-white' size={16} />}
  </Button>
);

interface StorySlideMediaProps {
  image: IImage;
  inverse?: boolean;
  isActive?: boolean;
  mediaType: "image" | "video";
  onVideoProgress?: (currentTime: number, duration: number, ended: boolean) => void;
  overlayGradientColor?: null | string;
  overlayText?: null | string;
  videoUrl?: string;
}

const StorySlideMedia = ({
  image,
  isActive,
  mediaType,
  onVideoProgress,
  videoUrl,
  overlayText,
  inverse,
  overlayGradientColor,
}: StorySlideMediaProps) => {
  const remoteRef = useRef<MediaRemoteControl | null>(null);

  useEffect(() => {
    if (remoteRef.current) {
      if (isActive) {
        remoteRef.current.play();
      } else {
        remoteRef.current.pause();
      }
    }
  }, [isActive]);

  const overlay = useMemo(() => {
    if (overlayText) {
      return (
        <div
          className='pointer-events-auto absolute inset-0 z-10 flex items-end justify-start p-8 lg:p-16'
          style={
            overlayGradientColor
              ? {
                  background: `linear-gradient(to top, ${overlayGradientColor} 0%, transparent 50%)`,
                }
              : undefined
          }
        >
          <p className='f-subhead-03 normal-case text-inverse md:f-subhead-01'>{overlayText}</p>
        </div>
      );
    }
  }, [overlayText, overlayGradientColor]);

  return (
    <div
      className={cn("relative w-full overflow-hidden rounded-inxl border border-default", inverse && "border-darker")}
    >
      {mediaType === "video" && videoUrl ? (
        <LazyVideoPlayer
          autoplay={isActive}
          controls={false}
          muted
          onProgress={isActive ? onVideoProgress : undefined}
          overlay={overlay}
          playButton={({ isPlaying, togglePlay }) => (
            <PlayPauseVideoButton isPlaying={isPlaying} togglePlay={togglePlay} />
          )}
          ratio='16x9'
          rounded={false}
          setRemote={remote => (remoteRef.current = remote)}
          src={videoUrl}
          unmuteButton={({ isMuted, toggleMute }) => <ToggleMuteButton isMuted={isMuted} onClick={toggleMute} />}
          withBorder={false}
          withUnmuteButton
        />
      ) : (
        <AspectRatio className='rounded-inxl' ratio='16x9' rounded={false}>
          <Image fit='cover' resource={image.resource} />
          {overlay}
        </AspectRatio>
      )}
    </div>
  );
};

// TODO we should spread link here but it breaks the cta section. Figure out why and fix
const Container = ({
  children,
  className,
  link,
  tabIndex,
}: {
  children: React.ReactNode;
  className?: string;
  link: ILink | null;
  tabIndex?: number;
}) =>
  link ? (
    <Link
      className={className}
      href={link.href}
      rel={link.rel}
      tabIndex={tabIndex}
      target={link.target}
      variant='clean'
    >
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );

interface StorySlideCTAProps {
  ctaLink: ILink | null;
  inverse?: boolean;
  isActive?: boolean;
  logo?: IImage;
  storyLink: ILink | null;
}

const CTAButton = ({
  ctaLink,
  isActive,
  size = "md",
}: {
  ctaLink: ILink | null;
  isActive?: boolean;
  size?: "md" | "sm";
}) => {
  if (!ctaLink) return null;
  return (
    <Link {...ctaLink} asButton buttonVariants={{ variant: "primary", size }} tabIndex={isActive ? undefined : -1}>
      {ctaLink.label}
    </Link>
  );
};

const StorySlideCTA = ({ ctaLink, isActive, logo, storyLink, inverse }: StorySlideCTAProps) => {
  return (
    <div className='flex h-36 items-center justify-between px-16 md:h-56'>
      <Container
        className='flex min-w-0 flex-1 items-center justify-start gap-16 overflow-hidden'
        link={storyLink}
        tabIndex={isActive ? undefined : -1}
      >
        {logo && (
          <div className='flex max-w-150 flex-shrink-0 items-center gap-8 rounded-lg border border-default bg-default px-12 py-4 md:py-8'>
            <Image className='h-20 w-4/5' fit='contain' sizes='20vw' {...logo} />
            <ArrowUpRight className='flex-shrink-0' size={16} />
          </div>
        )}
        <div className={cn("f-ui-02 min-w-0 flex-1 truncate text-light", inverse && "text-inverse")}>
          <span className='block truncate'>{storyLink?.label}</span>
        </div>
      </Container>
      <div className='hidden flex-shrink-0 lg:flex'>
        <CTAButton ctaLink={ctaLink} isActive={isActive} />
      </div>
    </div>
  );
};

interface StorySlideProps {
  inverse?: boolean;
  isActive?: boolean;
  item: StorySlidesBlock["sliderItems"][0];
  onVideoProgress?: (currentTime: number, duration: number, ended: boolean) => void;
  slideRef?: (el: HTMLDivElement | null) => void;
}

const StorySlide = ({ isActive, item, onVideoProgress, slideRef, inverse }: StorySlideProps) => {
  const videoUrl = item.video ? getVideoUrl(item.video) : undefined;
  const isLargeOrAbove = useIsLargeOrAbove();

  const ctaLink = useMemo(() => formatLink(item.cta?.ctaButton), [item.cta?.ctaButton]);
  const storyLink = useMemo(() => formatLink(item.cta?.storyCta?.storyLink), [item.cta?.storyCta?.storyLink]);

  return (
    <m.div
      animate={{
        opacity: isActive ? 1 : !isLargeOrAbove ? 0.3 : 0,
      }}
      aria-hidden={!isActive}
      className={cn(
        "story-slides-item relative flex flex-col rounded-xl bg-dark p-4",
        !isActive && "pointer-events-none",
        inverse && "bg-inverse-lighter",
      )}
      initial={{ opacity: !isLargeOrAbove ? 0.3 : 0 }}
      ref={slideRef}
      tabIndex={isActive ? 0 : -1}
      transition={{ duration: 0.5, ease: "linear" }}
    >
      <StorySlideMedia
        image={{ resource: item.image }}
        inverse={inverse}
        isActive={isActive}
        mediaType={item.mediaType}
        onVideoProgress={onVideoProgress}
        overlayGradientColor={item.overlayGradientColor}
        overlayText={item.overlayText}
        videoUrl={videoUrl}
      />
      {(ctaLink?.href || storyLink?.href) && (
        <StorySlideCTA
          ctaLink={ctaLink}
          inverse={inverse}
          isActive={isActive}
          logo={item.cta?.storyCta?.logo ? { resource: item.cta.storyCta.logo } : undefined}
          storyLink={storyLink}
        />
      )}
    </m.div>
  );
};

const MobileStorySlides = ({
  sliderItems,
  inverse,
}: {
  inverse?: boolean;
  sliderItems: StorySlidesBlock["sliderItems"];
}) => {
  const [visibleSlides, setVisibleSlides] = useState<Set<string>>(new Set());

  const observerRef = useRef<IntersectionObserver | null>(null);
  const slideRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (!sliderItems?.length) return;

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const slideId = entry.target.getAttribute("data-slide-id");
          if (!slideId) return;

          setVisibleSlides(prev => {
            const newSet = new Set(prev);
            if (entry.isIntersecting) {
              newSet.add(slideId);
            } else {
              newSet.delete(slideId);
            }
            return newSet;
          });
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.6,
      },
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sliderItems?.length]);

  const scrollToSlide = useCallback((slideId: string) => {
    const slideElement = slideRefs.current.get(slideId);
    if (slideElement) {
      slideElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, []);

  const items = sliderItems.map((item, index) => {
    const slideId = item.id ?? index.toString();
    return (
      <div
        className='flex h-full flex-col justify-between gap-32'
        key={slideId}
        ref={el => {
          if (el) {
            slideRefs.current.set(slideId, el);
            if (observerRef.current) {
              el.setAttribute("data-slide-id", slideId);
              observerRef.current.observe(el);
            }
          }
        }}
      >
        <div className='flex flex-col justify-between gap-8'>
          <StorySlideButton
            description={item.description}
            isActive={visibleSlides.has(slideId)}
            onClick={() => scrollToSlide(slideId)}
            slideId={slideId}
            title={item.title}
          />
          <CTAButton
            ctaLink={item.cta?.ctaButton ? formatLink(item.cta.ctaButton) : null}
            isActive={visibleSlides.has(slideId)}
            size='sm'
          />
        </div>
        <StorySlide inverse={inverse} isActive={visibleSlides.has(slideId)} item={item} />
      </div>
    );
  });
  return (
    <div className='lg:hidden'>
      <SliderGeneric items={items} largeCardsOnMobile largeCardsOnTablet onDark={inverse} />
    </div>
  );
};

export const BlockStorySlides = ({
  titlePrefix,
  titleLink,
  title,
  sliderItems,
  inverse,
}: StorySlidesBlock & { inverse?: boolean }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentSlideProgress, setCurrentSlideProgress] = useState(0);
  const [slideHeight, setSlideHeight] = useState<number>(0);

  const imageTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const activeSlideRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isLargeOrAbove = useIsLargeOrAbove();

  const isInView = useInView(containerRef, { once: true, threshold: 0.6 });

  const updateSlideHeight = useCallback(() => {
    if (isLargeOrAbove && activeSlideRef.current) {
      const height = activeSlideRef.current.offsetHeight;
      if (height > 0) {
        setSlideHeight(height);
      }
    } else if (!isLargeOrAbove) {
      setSlideHeight(0);
    }
  }, [isLargeOrAbove]);

  useEffect(() => {
    updateSlideHeight();
  }, [isLargeOrAbove, updateSlideHeight]);

  useLayoutEffect(() => {
    if (sliderItems?.length) {
      const timeoutId = setTimeout(() => {
        updateSlideHeight();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    if (!activeSlideRef.current) return;

    resizeObserverRef.current = new ResizeObserver(() => {
      requestAnimationFrame(updateSlideHeight);
    });

    resizeObserverRef.current.observe(activeSlideRef.current);

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [updateSlideHeight, isLargeOrAbove]);

  const clearTimers = useCallback(() => {
    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
  }, []);

  const goToNextSlide = useCallback(() => {
    if (!sliderItems?.length || !isLargeOrAbove) return;
    setActiveIndex(current => (current + 1) % sliderItems.length);
    setCurrentSlideProgress(0);
    lastCallTimeRef.current = 0;
  }, [sliderItems?.length, isLargeOrAbove]);

  useEffect(() => {
    const activeSlide = sliderItems?.[activeIndex];
    if (!activeSlide || !isLargeOrAbove || !isInView || activeSlide.mediaType !== "image") {
      clearTimers();
      return;
    }

    setCurrentSlideProgress(0);

    imageTimerRef.current = setTimeout(() => {
      goToNextSlide();
    }, 30000);

    const startTime = Date.now();
    const duration = 30000;

    progressIntervalRef.current = setInterval(() => {
      requestAnimationFrame(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setCurrentSlideProgress(progress);
      });
    }, 200);

    return () => {
      clearTimers();
    };
  }, [activeIndex, goToNextSlide, clearTimers, sliderItems, isLargeOrAbove, isInView]);

  const lastCallTimeRef = useRef(0);
  const onVideoProgress = useCallback(
    (currentTime: number, duration: number, ended: boolean) => {
      const now = Date.now();
      if (now - lastCallTimeRef.current >= 200 || (ended && currentSlideProgress > 0)) {
        lastCallTimeRef.current = now;
        requestAnimationFrame(() => {
          setCurrentSlideProgress(currentTime / duration);
          if (currentTime && duration && currentTime >= duration) {
            goToNextSlide();
          }
        });
      }
    },
    [goToNextSlide, currentSlideProgress],
  );

  const headerLink = useMemo(() => formatLink(titleLink), [titleLink]);

  if (!sliderItems?.length) return null;

  return (
    <Block name='story-slides'>
      <div className='mx-auto lg:container' ref={containerRef}>
        <HeadingWithLink headerLink={headerLink} inverse={inverse} titlePrefix={titlePrefix} />
        <h2 className='f-heading-03 mx-16 mb-52 max-w-3xl lg:mx-0'>{title}</h2>

        <MobileStorySlides inverse={inverse} sliderItems={sliderItems} />
        <div className='hidden grid-cols-5 gap-64 p-8 lg:grid'>
          <div className='col-span-2 flex flex-col gap-36'>
            {sliderItems.map((item, index) => (
              <StorySlideButton
                description={item.description}
                inverse={inverse}
                isActive={activeIndex === index}
                key={item.id ?? index.toString()}
                onClick={() => setActiveIndex(index)}
                progress={currentSlideProgress}
                slideId={item.id ?? index.toString()}
                title={item.title}
              />
            ))}
          </div>

          <div className='relative col-span-3 overflow-hidden' style={{ height: slideHeight || "auto" }}>
            <m.div
              className='absolute inset-0 flex flex-col'
              id='story-slides-track'
              initial={false}
              style={{
                transform: `translateY(-${activeIndex * 100}%)`,
              }}
            >
              {sliderItems.map((item, index) => (
                <StorySlide
                  inverse={inverse}
                  isActive={index === activeIndex}
                  item={item}
                  key={index}
                  onVideoProgress={onVideoProgress}
                  slideRef={index === activeIndex ? el => (activeSlideRef.current = el) : undefined}
                />
              ))}
            </m.div>
          </div>
        </div>
      </div>
    </Block>
  );
};
